import Vue from 'vue'
import api from './api'
import { mediaUrl } from './config'

/**
 * 内容结构（每页一个 scope）：
 * {
 *   texts:  { "页面原文案": "替换后的文案" },       // 文案层：自动接管页面静态文字
 *   fields: { "hero.title": "…", "rows": [...] },  // 数据层：结构化字段与列表
 *   assets: { "--cms-img-bg": "https://…/x.png" }  // 图层：覆盖样式里用 var() 暴露的背景图/视频
 * }
 */

const EMPTY = { texts: {}, fields: {}, assets: {} }

function getPath(obj, path) {
  if (!obj || !path) return undefined
  const segs = path.split('.')
  let cur = obj
  for (let i = 0; i < segs.length; i++) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = cur[segs[i]]
  }
  return cur
}

function setPath(obj, path, value) {
  const segs = path.split('.')
  let cur = obj
  for (let i = 0; i < segs.length - 1; i++) {
    const k = segs[i]
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {}
    cur = cur[k]
  }
  cur[segs[segs.length - 1]] = value
  return obj
}

export const state = Vue.observable({
  ready: false,     // 是否已尝试拉取过远端内容
  online: false,    // 远端是否可用（Electron 离线时 false，页面回落默认值）
  version: 0,       // 每次远端内容刷新自增，供文案/图层重刷
  bundle: {},       // { scope: {texts, fields, assets} }
  discovered: {},   // { scope: {原文案: true} } 运行时扫描到的文案清单
  dirty: {},        // { scope: true } 后台编辑未保存的标记
})

function ensure(scope) {
  if (!state.bundle[scope]) Vue.set(state.bundle, scope, JSON.parse(JSON.stringify(EMPTY)))
  const b = state.bundle[scope]
  if (!b.texts) Vue.set(b, 'texts', {})
  if (!b.fields) Vue.set(b, 'fields', {})
  if (!b.assets) Vue.set(b, 'assets', {})
  return b
}

/** 拉取全部远端内容（只读接口，任何失败都静默降级为本地默认内容） */
export async function load() {
  state.ready = true
  try {
    const all = await api.fetchAll()
    if (all && typeof all === 'object') {
      // 用新对象整体替换，保证 Vue 能追踪到新增 scope
      Vue.set(state, 'bundle', Object.keys(all).reduce((acc, scope) => {
        acc[scope] = Object.assign(JSON.parse(JSON.stringify(EMPTY)), all[scope] || {})
        return acc
      }, {}))
      state.online = true
      state.version++
      return true
    }
  } catch (e) {
    state.online = false
    console.warn('[cms] 远端内容拉取失败，使用本地默认内容：', e.message)
  }
  return false
}

/** 取结构化字段：远端未配置时回落到传入的默认值 */
export function field(scope, path, fallback) {
  const v = getPath(state.bundle[scope] && state.bundle[scope].fields, path)
  return v === undefined || v === null || v === '' ? fallback : v
}

/** 取列表字段，保证返回数组 */
export function list(scope, path, fallback) {
  const v = field(scope, path, undefined)
  if (Array.isArray(v) && v.length) return v
  return Array.isArray(fallback) ? fallback : []
}

/** 文案接管：未配置则原样返回 */
export function text(scope, original) {
  const t = state.bundle[scope] && state.bundle[scope].texts
  if (!t) return original
  const v = t[original]
  // 空串视为「未配置」而不是「清空」，避免后台清空文案后大屏文字消失
  return v === undefined || v === null || v === '' ? original : String(v)
}

/** 图层：返回素材地址（未配置返回空串，由调用方决定要不要回落） */
export function asset(name) {
  for (const scope of Object.keys(state.bundle)) {
    const a = state.bundle[scope] && state.bundle[scope].assets
    if (a && a[name]) return a[name]
  }
  return ''
}

/** 图层带回落：后台配置了就换成配置的素材地址，否则用页面内置默认值（<video>/<img> 的 src 用） */
export function assetOr(name, fallback) {
  const v = asset(name)
  if (!v) return fallback
  if (/^(https?:)?\/\//.test(v) || v.startsWith('data:')) return v
  return mediaUrl(v)
}

/** 把当前运行时扫描到的文案清单补登记到服务端，供后台下拉选择 */
let flushTimers = {}
export function markDiscovered(scope, str) {
  if (!str) return
  if (!state.discovered[scope]) Vue.set(state.discovered, scope, {})
  if (state.discovered[scope][str]) return
  Vue.set(state.discovered[scope], str, true)
  clearTimeout(flushTimers[scope])
  flushTimers[scope] = setTimeout(() => flushDiscovered(scope), 2500)
}

function flushDiscovered(scope) {
  const keys = Object.keys(state.discovered[scope] || {})
  if (!keys.length) return
  if (!state.online) return // 服务不可用时不打扰大屏
  const payload = keys.slice(0, 800)
  api.postTexts(scope, payload).catch(() => { /* 登记失败不影响展示 */ })
}

/** 后台写入：fields / texts / assets */
export function set(scope, kind, path, value) {
  const b = ensure(scope)
  if (kind === 'fields') {
    Vue.set(b, 'fields', JSON.parse(JSON.stringify(b.fields)))
    setPath(b.fields, path, value)
  } else if (kind === 'texts') {
    Vue.set(b.texts, path, value)
  } else if (kind === 'assets') {
    Vue.set(b.assets, path, value)
  }
  Vue.set(state.dirty, scope, true)
  return b
}

export function replace(scope, content) {
  const b = ensure(scope)
  const next = {
    texts: (content && content.texts) || {},
    fields: (content && content.fields) || {},
    assets: (content && content.assets) || {},
  }
  Vue.set(state.bundle, scope, next)
  Vue.set(state.dirty, scope, false)
  state.version++
}

export async function save(scope) {
  const b = ensure(scope)
  const res = await api.saveScope(scope, b)
  Vue.set(state.dirty, scope, false)
  state.version++
  return res
}

export function isDirty(scope) {
  return !!state.dirty[scope]
}

export default { state, load, field, list, text, asset, set, replace, save, isDirty, markDiscovered }
